import {makeAutoObservable} from "mobx";
import dayjs from "dayjs";
import { clearPendingPokoroProfileReturn } from "../common/pokoroProfileReturnFlow";
import EnFilter from "bad-words";
import KoFilter from "badwords-ko";

export const State = {
    Authenticated: 'Authenticated',
    NotAuthenticated: 'NotAuthenticated',
    Pending: 'Pending',
    Failed: 'Failed',
    Disabled: 'Disabled',
};

export const LocalStorageTokenKey = '_AUTHENTICATION_TOKEN_';
export const LocalStorageLoginType = '_AUTHENTICATION_LOGIN_TYPE_';
export const LocalStorageRequestLoginType = '_AUTHENTICATION_REQUEST_LOGIN_TYPE_';
export const LocalStorageConnectSkip = '_CONNECT_SKIP_';
export const LocalStorageKeepLogin = '_KEEP_LOGIN_';
export const LocalStorageNdpAccessToken = '_NDP_ACCESS_TOKEN_';
export const LocalStorageNdpRefreshToken = '_NDP_REFRESH_TOKEN_';
export const LocalStorageNdpRefreshTokenExpireDatetime = '_NDP_REFRESH_TOKEN_EXPIRE_DATETIME';
export const LocalStorageAgentServerList = '_AGENT_SERVER_LIST_';

const EmptyLogin = {
    loginType: '',
    code: ''
};

const EmptyUser = {
    id: '',
    nickname: '',
    allowTermsOfAge: false,
    allowTermsOfUse: false,
    allowTermsOfPrivacy: false,
    enabled: false,
    legacy: false,
    birthDate: '',
    defaultPenName: '',
    ageOver14: false,
    ageAgreedToUseUnder14: false,
    ageAgreementDate: '',
    loginType: '',
    profileImageUrl: '',
    createdDatetime: '',
    updatedDatetime: '',
    profileExists: false,
};

const logPrefix  = "[AuthStore]"
export default class AuthStore {
    constructor(props) {
        this.authRepository = props.authRepository;
        this.userStore = props.userStore;
        this.homeStore = props.homeStore;
        this.conversationStore = props.conversationStore;
        this.pronunciationStore = props.pronunciationStore;
        this.reportStore = props.reportStore;

        this.enFilter = new EnFilter();
        this.koFilter = new KoFilter();

        this.init();
        // this.loginType = '';
        // this.loginCode = '';
        // this.login = Object.assign({}, EmptyLogin);
        // this.loginState = State.NotAuthenticated;
        // this.loginToken = '';
        // this.loginUser = Object.assign({}, EmptyUser);
        //
        // this.termsOfAge = false;
        // this.termsOfUse = false;
        // this.termsOfPrivacy = false;
        // this.nickname = '';
        // this.penName = '';
        // this.birthDate = '';
        // this.gender = '';
        // this.ageOver14 = '';
        // this.ageAgreedToUseUnder14 = '';
        // this.isMemoryEnabled = false;

        makeAutoObservable(this);
    }

    init = () => {
        this.loginType = '';
        this.loginCode = '';
        this.login = Object.assign({}, EmptyLogin);
        this.loginState = State.NotAuthenticated;
        this.loginToken = '';
        this.loginUser = Object.assign({}, EmptyUser);

        this.termsOfAge = false;
        this.termsOfUse = false;
        this.termsOfPrivacy = false;
        this.nickname = '';
        this.penName = '';
        this.birthDate = '';
        this.gender = '';
        this.ageOver14 = '';
        this.ageAgreedToUseUnder14 = '';
        this.isMemoryEnabled = false;
        this.isWebSearchEnabled = false;

        clearPendingPokoroProfileReturn();
    };

    initProfileInfo = () => {
        this.nickname = '';
        this.penName = '';
        this.birthDate = '';
        this.gender = '';
        this.ageOver14 = '';
        this.ageAgreedToUseUnder14 = '';
        this.isMemoryEnabled = false;
        this.isWebSearchEnabled = false;
    }


    invalidateLogin = () => {
        this.init();
        // this.login = Object.assign({}, EmptyLogin);
        // this.loginState = State.NotAuthenticated;
        // this.loginToken = '';
        // this.loginUser = Object.assign({}, EmptyUser);
    };

    setLoginType = (type) => {
        this.loginType = type;
    }

    setLoginCode = (code) => {
        this.loginCode = code;
    }

    setTermsOfAll = (agree) => {
        this.termsOfAge = agree;
        this.termsOfUse = agree;
        this.termsOfPrivacy = agree;
    }

    setTermsOfAge = (agree) => {
        this.termsOfAge = agree;
    }

    setTermsOfUse = (agree) => {
        this.termsOfUse = agree;
    }

    setTermsOfPrivacy = (agree) => {
        this.termsOfPrivacy = agree;
    }

    setNickname = (nickname) => {
        if (nickname.length <= 16) {
            this.nickname = nickname;
        }
    }

    setPenName = (penName) => {
        if (penName.length <= 16) {
            this.penName = penName;
        }
    }

    setBirthDate = (birthDate) => {
        this.birthDate = birthDate;
    }

    setGender = (gender) => {
        this.gender = gender;
    }

    setAgeAgreedToUseUnder14 = (agreed) => {
        this.ageAgreedToUseUnder14 = agreed;
    }

    setAgeOver14 = (isOver14) => {
        this.ageOver14 = isOver14;
    }

    setIsMemoryEnabled = (enabled) => {
        this.isMemoryEnabled = enabled;
    }

    setIsWebSearchEnabled = (enabled) => {
        this.isWebSearchEnabled = enabled;
    }

    setLoginState = (state) => {
        this.loginState = state;
    }

    isNotAcceptableNickname = (intl, nickname) => {
        if (nickname !== '') {
            const specialCharactersRegex = /[\s!@#$%^&*(),.?":{}|<>]/;
            if (specialCharactersRegex.test(nickname)) {
                return intl.formatMessage({ id: "invalid_nickname_special_characters" });
            }

            const incompleteHangulRegex = /[ㄱ-ㅎㅏ-ㅣ]/;
            if (incompleteHangulRegex.test(nickname)) {
                return intl.formatMessage({ id: "invalid_nickname_incorrect" });
            }

            if (nickname.length < 2) {
                return intl.formatMessage({ id: "invalid_nickname_not_enough" });
            }

            if (this.enFilter.isProfane(nickname) || this.koFilter.isProfane(nickname)) {
                return intl.formatMessage({ id: "invalid_nickname_profanity" });
            }
        }

        return '';
    }

    isNotAcceptablePenName = (intl, penName) => {
        if (penName !== '') {
            const specialCharactersRegex = /[\s!@#$%^&*(),.?":{}|<>]/;
            if (specialCharactersRegex.test(penName)) {
                return intl.formatMessage({ id: "invalid_nickname_special_characters" });
            }

            const incompleteHangulRegex = /[ㄱ-ㅎㅏ-ㅣ]/;
            if (incompleteHangulRegex.test(penName)) {
                return intl.formatMessage({ id: "invalid_nickname_incorrect" });
            }

            if (penName.length < 2) {
                return intl.formatMessage({ id: "invalid_nickname_not_enough" });
            }

            if (this.enFilter.isProfane(penName) || this.koFilter.isProfane(penName)) {
                return intl.formatMessage({ id: "invalid_pen_name_profanity" });
            }
        }

        return '';
    }

    * doLogin(history, callback) {
        this.loginState = State.Pending;

        try {
            console.log(logPrefix, 'Start doLogin.');

            this.login.loginType = this.loginType;
            this.login.code = this.loginCode;
            const param = this.login;
            const response = yield this.authRepository.login(param);

            const token = response.data.token;
            const user = response.data.user;

            localStorage.setItem(LocalStorageTokenKey, token);
            localStorage.setItem(LocalStorageLoginType, this.loginType);

            console.log('doLogin');
            console.log(this);

            this.loginState = State.Authenticated;
            this.loginToken = token;
            this.loginUser = user;
            this.loginCode = '';

            const accessToken = response.data.ndpAccessToken;
            const refreshToken = response.data.ndpRefreshToken;
            const agentServerList = response.data.agentServerList;
            localStorage.setItem(LocalStorageNdpAccessToken, accessToken);
            localStorage.setItem(LocalStorageNdpRefreshToken, refreshToken);
            localStorage.setItem(LocalStorageNdpRefreshTokenExpireDatetime, dayjs().add(6, 'month').add(-1, 'day'));
            localStorage.setItem(LocalStorageAgentServerList, JSON.stringify(agentServerList));

            console.log(logPrefix, 'Finished doLogin.');

            callback && callback();
        } catch (e) {
            console.log(logPrefix, 'Failed doLogin.');

            // this.loginToken = '';
            // this.loginCode = '';
            // this.loginUser = Object.assign({}, EmptyUser);
            this.init();

            if (e.response && e.response.data && e.response.data.errorCode === 'DisabledUser') {
                this.loginState = State.Disabled;
            } else {
                this.loginState = State.Failed;
            }
        }
    }

    * autoLogin(loginType, ndpRefreshToken, callback) {
        this.loginState = State.Pending;

        try {
            console.log(logPrefix, 'Start autoLogin.');

            const param = {loginType: loginType, ndpRefreshToken: ndpRefreshToken}
            const response = yield this.authRepository.autoLogin(param);

            const token = response.data.token;
            const user = response.data.user;

            localStorage.setItem(LocalStorageTokenKey, token);
            localStorage.setItem(LocalStorageLoginType, localStorage.getItem(LocalStorageLoginType));

            console.log('doLogin');
            console.log(this);

            this.loginState = State.Authenticated;
            this.loginToken = token;
            this.loginUser = user;

            const accessToken = response.data.ndpAccessToken;
            const agentServerList = response.data.agentServerList;
            localStorage.setItem(LocalStorageNdpAccessToken, accessToken);
            localStorage.setItem(LocalStorageAgentServerList, JSON.stringify(agentServerList));

            console.log(logPrefix, 'Finished autoLogin.');

            callback && callback();
        } catch (e) {
            console.log(logPrefix, 'Failed autoLogin.');
            // this.loginState = State.Failed;
            // this.loginToken = '';
            // this.loginUser = Object.assign({}, EmptyUser);
            this.init();
        }
    }

    * checkLogin() {
        const token = localStorage.getItem(LocalStorageTokenKey);

        if(token) {
            // 새로고침/재진입 시 checkLogin 응답 전까지 라우팅이 흔들리며
            // Home이 잠깐 마운트되는 것을 막기 위해 Pending으로 전환
            this.loginState = State.Pending;
            try {
                const response = yield this.authRepository.checkLogin();

                const user = response.data;
                this.loginState = State.Authenticated;
                this.loginUser = user;
            } catch(e) {
                // this.loginState = State.NotAuthenticated;
                // this.loginToken = '';
                // this.loginUser = Object.assign({}, EmptyUser);
                this.init();
            }
        }
    }

    * doLogout(callback) {
        try {
            yield this.authRepository.logout();

            console.log(this);
            // this.login = Object.assign({}, EmptyLogin);
            // this.loginState = State.NotAuthenticated;
            // this.loginToken = '';
            // this.loginUser = Object.assign({}, EmptyUser);
            this.init();

            this.userStore.init();
            this.homeStore.init();
            this.conversationStore.init();

            this.pronunciationStore.initDate();
            this.pronunciationStore.init();

            this.reportStore.initDate();
            this.reportStore.init();

            callback && callback();
        } catch(e) {
            // this.login = Object.assign({}, EmptyLogin);
            // this.loginState = State.NotAuthenticated;
            // this.loginToken = '';
            // this.loginUser = Object.assign({}, EmptyUser);
            this.init();
        } finally {
            localStorage.removeItem(LocalStorageTokenKey);
            localStorage.removeItem(LocalStorageNdpAccessToken);
            localStorage.removeItem(LocalStorageNdpRefreshToken);
            localStorage.removeItem(LocalStorageNdpRefreshTokenExpireDatetime);
            localStorage.removeItem(LocalStorageAgentServerList);
            localStorage.removeItem(LocalStorageKeepLogin);
        }
    }

    * agreeTerms(callback) {
        try {
            console.log(logPrefix, 'Start agreeTerms.');

            const param = {userId: this.loginUser.id, isAllowTermsOfAge: this.termsOfAge, isAllowTermsOfUse: this.termsOfUse, isAllowTermsOfPrivacy: this.termsOfPrivacy, loginType: this.loginUser.loginType, profileImageUrl: this.loginUser.profileImageUrl}
            yield this.authRepository.agreeTerms(this.loginUser.id, param);

            this.termsOfAge = false;
            this.termsOfUse = false;
            this.termsOfPrivacy = false;

            console.log(logPrefix, 'Finished agreeTerms.');

            callback && callback();
            // this.checkLogin();
        } catch(e) {
            console.log(logPrefix, 'Failed agreeTerms. error: ' + e);
        }
    }

    * updateUserInfo(isAgeOver14, isAgeAgreedToUseUnder14, callback) {
        try {
            console.log(logPrefix, 'Start updateUserInfo.');

            const param = {
                userId: this.loginUser.id,
                nickname: this.nickname,
                birthDate: this.birthDate,
                gender: this.gender,
                defaultPenName: this.penName,
                isAgeOver14: isAgeOver14,
                isAgeAgreedToUseUnder14: isAgeAgreedToUseUnder14,
                isMemoryDisabled: !this.isMemoryEnabled,
                isWebSearchDisabled: !this.isWebSearchEnabled,
                loginType: this.loginUser.loginType,
                profileImageUrl: this.loginUser.profileImageUrl
            }

            if (isAgeOver14 === undefined) {
                param.isAgeOver14 = false;
            }
            if (isAgeAgreedToUseUnder14 === undefined) {
                param.isAgeAgreedToUseUnder14 = false;
            }

            const response = yield this.authRepository.updateUserInfo(this.loginUser.id, param);
            this.loginUser = response.data;

            this.loginUser.nickname = this.nickname;
            this.loginUser.birthDate = this.birthDate;

            this.nickname = '';
            this.birthDate = '';
            this.penName = '';
            this.gender = '';
            this.ageOver14 = '';
            this.ageAgreedToUseUnder14 = '';
            this.isMemoryEnabled = false;

            console.log(logPrefix, 'Finished updateUserInfo.');

            callback && callback();
        } catch(e) {
            console.log(logPrefix, 'Failed updateNickname. error: ' + e);
        }
    }

    * saveUserDeviceProfile(deviceId, callback) {
        try {
            console.log(logPrefix, 'Start saveUserDeviceProfile.');

            const gender = this.gender === 'boy' ? 'M' : (this.gender === 'girl' ? 'F' : 'U');
            const param = {
                deviceId: deviceId ?? '',
                name: this.nickname,
                penName: this.penName === '' ? null : this.penName,
                birthDate: this.birthDate,
                gender: gender,
                isAgreedToUseUnder14: this.ageAgreedToUseUnder14 === true,
                isMemoryDisabled: !this.isMemoryEnabled,
                isWebSearchDisabled: !this.isWebSearchEnabled,
                loginType: this.loginUser.loginType,
                profileImageUrl: this.loginUser.profileImageUrl
            };

            const response = yield this.authRepository.saveUserDeviceProfile(this.loginUser.id, param);
            this.loginUser = response.data;

            this.nickname = '';
            this.penName = '';
            this.birthDate = '';
            this.gender = '';
            this.ageOver14 = '';
            this.ageAgreedToUseUnder14 = '';
            this.isMemoryEnabled = false;
            this.isWebSearchEnabled = false;

            console.log(logPrefix, 'Finished saveUserDeviceProfile.');

            callback && callback(response);
        } catch (e) {
            console.log(logPrefix, 'Failed saveUserDeviceProfile. error: ' + e);
            callback && callback(null);
        }
    }

    * saveUserDeviceProfiles(deviceIds, callback) {
        try {
            console.log(logPrefix, 'Start saveUserDeviceProfiles.');

            const param = {
                deviceIds: Array.isArray(deviceIds) ? deviceIds : [],
                name: this.nickname,
                penName: this.penName === '' ? null : this.penName,
                birthDate: this.birthDate,
                gender: this.gender === 'boy' ? 'M' : (this.gender === 'girl' ? 'F' : 'U'),
                isAgreedToUseUnder14: this.ageAgreedToUseUnder14 === true,
                isMemoryDisabled: !this.isMemoryEnabled,
                isWebSearchDisabled: !this.isWebSearchEnabled,
                loginType: this.loginUser.loginType,
                profileImageUrl: this.loginUser.profileImageUrl
            };

            const response = yield this.authRepository.saveUserDeviceProfiles(this.loginUser.id, param);
            this.loginUser = response.data;

            this.nickname = '';
            this.penName = '';
            this.birthDate = '';
            this.gender = '';
            this.ageOver14 = '';
            this.ageAgreedToUseUnder14 = '';
            this.isMemoryEnabled = false;
            this.isWebSearchEnabled = false;

            console.log(logPrefix, 'Finished saveUserDeviceProfiles.');

            callback && callback(response);
        } catch (e) {
            console.log(logPrefix, 'Failed saveUserDeviceProfiles. error: ' + e);
            callback && callback(null);
        }
    }

    * updateUserDeviceProfile(deviceId, callback) {
        try {
            console.log(logPrefix, 'Start updateUserDeviceProfile.');

            const gender = this.gender === 'boy' ? 'M' : (this.gender === 'girl' ? 'F' : 'U');
            const param = {
                deviceId: deviceId,
                name: this.nickname,
                birthDate: this.birthDate,
                gender: gender,
                isAgreedToUseUnder14: this.ageAgreedToUseUnder14 === true
            };

            const response = yield this.authRepository.updateUserDeviceProfile(this.loginUser.id, param);

            this.nickname = '';
            this.birthDate = '';
            this.gender = '';
            this.ageOver14 = '';
            this.ageAgreedToUseUnder14 = '';

            console.log(logPrefix, 'Finished updateUserDeviceProfile.');

            callback && callback(response);
        } catch (e) {
            console.log(logPrefix, 'Failed updateUserDeviceProfile. error: ' + e);
        }
    }

    * completeSignupWithUserDeviceProfile(deviceId, callback) {
        try {
            console.log(logPrefix, 'Start completeSignupWithUserDeviceProfile.');

            const gender = this.gender === 'boy' ? 'M' : (this.gender === 'girl' ? 'F' : 'U');
            const param = {
                agreeTerms: {
                    isAllowTermsOfAge: this.termsOfAge,
                    isAllowTermsOfUse: this.termsOfUse,
                    isAllowTermsOfPrivacy: this.termsOfPrivacy,
                    loginType: this.loginUser.loginType,
                    profileImageUrl: this.loginUser.profileImageUrl
                },
                profile: {
                    deviceId: deviceId ?? '',
                    name: this.nickname,
                    penName: this.penName === '' ? null : this.penName,
                    birthDate: this.birthDate,
                    gender: gender,
                    isAgreedToUseUnder14: this.ageAgreedToUseUnder14 === true,
                    isMemoryDisabled: !this.isMemoryEnabled,
                    isWebSearchDisabled: !this.isWebSearchEnabled,
                    loginType: this.loginUser.loginType,
                    profileImageUrl: this.loginUser.profileImageUrl
                }
            };

            const response = yield this.authRepository.completeSignupWithUserDeviceProfile(this.loginUser.id, param);
            this.loginUser = response.data;

            this.termsOfAge = false;
            this.termsOfUse = false;
            this.termsOfPrivacy = false;

            this.nickname = '';
            this.penName = '';
            this.birthDate = '';
            this.gender = '';
            this.ageOver14 = '';
            this.ageAgreedToUseUnder14 = '';
            this.isMemoryEnabled = false;
            this.isWebSearchEnabled = false;

            console.log(logPrefix, 'Finished completeSignupWithUserDeviceProfile.');

            callback && callback(response);
        } catch (e) {
            console.log(logPrefix, 'Failed completeSignupWithUserDeviceProfile. error: ' + e);
        }
    }

    * updateUserAgeAgreement(isAgeOver14, isAgeAgreedToUseUnder14, callback) {
        try {
            console.log(logPrefix, 'Start updateUserAgeAgreement.');

            const param = {
                userId: this.loginUser.id,
                isOver14: isAgeOver14,
                isAgreedToUseUnder14: isAgeAgreedToUseUnder14,
                loginType: this.loginUser.loginType,
                profileImageUrl: this.loginUser.profileImageUrl
            }

            const response = yield this.authRepository.updateUserAgeAgreement(this.loginUser.id, param);
            this.loginUser = response.data;

            console.log(logPrefix, 'Finished updateUserAgeAgreement.');

            callback && callback();
        } catch(e) {
            console.log(logPrefix, 'Failed updateUserAgeAgreement. error: ' + e);
        }
    }
}
